import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';

const AdminRoutes = () => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null); // 'approve' or 'reject'
  const [modalRouteId, setModalRouteId] = useState(null);
  const [modalData, setModalData] = useState({
    sinister: '',
    comments: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [paidRouteIds, setPaidRouteIds] = useState(new Set());

  const fetchTransactions = async () => {
    try {
      const res = await axios.get('/api/admin/transactions');
      const paidIds = new Set();
      res.data.forEach(transaction => {
        if (transaction.routeIds && transaction.routeIds.length > 0) {
          transaction.routeIds.forEach(routeId => {
            // routeId should be a string from the backend, but handle other formats just in case
            const routeIdStr = typeof routeId === 'string' 
              ? routeId 
              : (routeId && routeId.toString ? routeId.toString() : String(routeId));
            
            if (routeIdStr && routeIdStr !== 'null' && routeIdStr !== 'undefined') {
              paidIds.add(routeIdStr);
            }
          });
        }
      });
      console.log('Paid route IDs:', Array.from(paidIds));
      setPaidRouteIds(paidIds);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/admin/routes');
      console.log('Fetched routes:', res.data.length);
      setRoutes(res.data);
    } catch (error) {
      console.error('Error fetching routes:', error);
      alert('Error al cargar rutas: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
    fetchRoutes();
  }, []);

  // Refresh routes when navigating back from route designation
  useEffect(() => {
    if (location.state?.refresh) {
      // Small delay to ensure backend has saved the route
      setTimeout(() => {
        fetchTransactions();
        fetchRoutes();
      }, 500);
    }
  }, [location]);

  const handleOpenModal = (type, routeId) => {
    setModalType(type);
    setModalRouteId(routeId);
    setModalData({ sinister: '', comments: '' });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalType(null);
    setModalRouteId(null);
    setModalData({ sinister: '', comments: '' });
  };

  const handleSubmitModal = async () => {
    if (!modalType || !modalRouteId) return;
    
    // Validate required fields when rejecting
    if (modalType === 'reject') {
      if (!modalData.sinister || !modalData.sinister.trim()) {
        alert('El campo Siniestro es requerido al rechazar la ruta');
        return;
      }
      if (!modalData.comments || !modalData.comments.trim()) {
        alert('El campo Comentarios es requerido al rechazar la ruta');
        return;
      }
    }
    
    setSubmitting(true);
    try {
      let endpoint;
      if (modalType === 'approve') {
        endpoint = `/api/admin/routes/${modalRouteId}/approve-waybill`;
      } else if (modalType === 'reject') {
        endpoint = `/api/admin/routes/${modalRouteId}/reject-waybill`;
      } else if (modalType === 'return') {
        endpoint = `/api/admin/routes/${modalRouteId}/return-waybill`;
      }
      
      await axios.put(endpoint, modalData);
      handleCloseModal();
      fetchTransactions();
      fetchRoutes();
      alert(modalType === 'approve' 
        ? 'Ruta aprobada exitosamente' 
        : modalType === 'reject'
        ? 'Ruta rechazada exitosamente'
        : 'Guía devuelta para corrección exitosamente');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.[0]?.msg || 
                          `Error al ${modalType === 'approve' ? 'aprobar' : modalType === 'reject' ? 'rechazar' : 'devolver'} la ruta`;
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveWaybill = (id) => {
    handleOpenModal('approve', id);
  };

  const handleRejectWaybill = (id) => {
    handleOpenModal('reject', id);
  };

  const handleReturnWaybill = (id) => {
    handleOpenModal('return', id);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': 'Pendiente',
      'approved': 'Aprobada',
      'pending-acceptance': 'Pendiente Aceptación',
      'accepted': 'Aceptada',
      'in-progress': 'En Progreso',
      'pending-approval': 'Pendiente Aprobación',
      'completed': 'Completada',
      'rejected': 'Rechazada',
      'returned': 'Devuelta'
    };
    // Always use the statusMap, default to 'Pendiente' if status not found
    const statusText = statusMap[status] || 'Pendiente';
    
    // Ensure pending status shows as 'pending' class, not something else
    let statusClass = 'badge badge-pending';
    if (status === 'approved') statusClass = 'badge badge-approved';
    else if (status === 'accepted') statusClass = 'badge badge-accepted';
    else if (status === 'in-progress') statusClass = 'badge badge-in-progress';
    else if (status === 'pending-approval') statusClass = 'badge badge-pending-approval';
    else if (status === 'pending-acceptance') statusClass = 'badge badge-pending-acceptance';
    else if (status === 'completed') statusClass = 'badge badge-completed';
    else if (status === 'rejected') statusClass = 'badge badge-rejected';
    else if (status === 'returned') statusClass = 'badge badge-warning';
    
    // Return badge with explicit text (not relying on CSS uppercase transform for display)
    return <span className={statusClass} style={{ textTransform: 'none' }}>{statusText}</span>;
  };

  const getPayStateBadge = (route) => {
    // Only show pay state for completed or rejected routes
    if (route.status !== 'completed' && route.status !== 'rejected') {
      return <span style={{ color: '#999' }}>-</span>;
    }
    
    // Get route ID as string - handle both _id object and string formats
    let routeId = null;
    if (route._id) {
      if (typeof route._id === 'string') {
        routeId = route._id;
      } else if (route._id.toString) {
        routeId = route._id.toString();
      }
    }
    
    if (!routeId) {
      return <span className="badge badge-pending" style={{ textTransform: 'none' }}>No Pagado</span>;
    }
    
    const isPaid = paidRouteIds.has(routeId);
    
    if (isPaid) {
      return <span className="badge badge-completed" style={{ textTransform: 'none' }}>Pagado</span>;
    } else {
      return <span className="badge badge-pending" style={{ textTransform: 'none' }}>No Pagado</span>;
    }
  };

  if (loading) {
    return <div className="loading">Cargando rutas...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <h2 style={{ margin: 0 }}>Todas las Rutas</h2>
        <button className="btn btn-secondary" onClick={fetchRoutes} style={{ minWidth: '100px' }}>
          Actualizar
        </button>
      </div>
      {routes.length === 0 ? (
        <div className="card">
          <p>No se encontraron rutas.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="table responsive-table">
          <thead>
            <tr>
              <th>Número de Ruta</th>
              <th>Destino</th>
              <th>Estado</th>
              <th>Estado de Pago</th>
              <th>Transporte</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {routes.map(route => (
              <tr key={route._id}>
                <td data-label="Número de Ruta">{route.routeNumber}</td>
                <td data-label="Destino">{route.endPoint}</td>
                <td data-label="Estado">{getStatusBadge(route.status)}</td>
                <td data-label="Estado de Pago">{getPayStateBadge(route)}</td>
                <td data-label="Transporte">
                  {route.assignedTo ? (route.assignedTo.companyName || route.assignedTo.name) : (
                    route.acceptanceRequests && route.acceptanceRequests.filter(req => req.status === 'pending').length > 0
                      ? `${route.acceptanceRequests.filter(req => req.status === 'pending').length} solicitud(es) pendiente(s)`
                      : 'No asignada'
                  )}
                </td>
                <td data-label="Acciones">
                  <div className="flex-container" style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Link to={`/admin/routes/${route._id}`} className="btn btn-primary" style={{ padding: '5px 10px', fontSize: '14px', flex: '1', minWidth: '60px' }}>
                      Ver
                    </Link>
                    {route.status === 'pending-approval' && (
                      <>
                        <button 
                          className="btn btn-success" 
                          onClick={() => handleApproveWaybill(route._id)}
                          style={{ padding: '5px 10px', fontSize: '14px' }}
                        >
                          Aprobar Guía
                        </button>
                        <button 
                          className="btn btn-warning" 
                          onClick={() => handleReturnWaybill(route._id)}
                          style={{ padding: '5px 10px', fontSize: '14px', backgroundColor: '#f59e0b', color: 'white', border: 'none' }}
                        >
                          Devolver
                        </button>
                        <button 
                          className="btn btn-danger" 
                          onClick={() => handleRejectWaybill(route._id)}
                          style={{ padding: '5px 10px', fontSize: '14px' }}
                        >
                          Rechazar
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          </table>
        </div>
      )}

      {/* Modal for Approve/Reject */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            position: 'relative',
            border: '1px solid #e5e7eb'
          }}>
            <button
              onClick={handleCloseModal}
              disabled={submitting}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                opacity: submitting ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (!submitting) {
                  e.target.style.background = '#dc2626';
                  e.target.style.transform = 'scale(1.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (!submitting) {
                  e.target.style.background = '#ef4444';
                  e.target.style.transform = 'scale(1)';
                }
              }}
            >
              ×
            </button>
            
            <h3 style={{ 
              marginBottom: '24px', 
              color: modalType === 'approve' ? '#10b981' : '#ef4444',
              fontSize: '24px',
              fontWeight: '600',
              paddingRight: '40px'
            }}>
              {modalType === 'approve' ? '✓ Aprobar Ruta' : '✕ Rechazar Ruta'}
            </h3>
            
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600', 
                color: '#374151',
                fontSize: '14px'
              }}>
                Siniestro {modalType === 'reject' && <span style={{ color: '#ef4444' }}>*</span>}
              </label>
              <input
                type="text"
                value={modalData.sinister}
                onChange={(e) => setModalData({ ...modalData, sinister: e.target.value })}
                placeholder={modalType === 'reject' ? 'Información sobre siniestros (requerido)' : 'Información sobre siniestros (opcional)'}
                required={modalType === 'reject'}
                disabled={submitting}
                style={{ 
                  width: '100%', 
                  padding: '12px 16px', 
                  border: '2px solid #e5e7eb', 
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            
            <div className="form-group" style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600', 
                color: '#374151',
                fontSize: '14px'
              }}>
                Comentarios {modalType === 'reject' && <span style={{ color: '#ef4444' }}>*</span>}
              </label>
              <textarea
                value={modalData.comments}
                onChange={(e) => setModalData({ ...modalData, comments: e.target.value })}
                placeholder={modalType === 'reject' ? 'Comentarios adicionales (requerido)' : 'Comentarios adicionales (opcional)'}
                required={modalType === 'reject'}
                disabled={submitting}
                rows="4"
                style={{ 
                  width: '100%', 
                  padding: '12px 16px', 
                  border: '2px solid #e5e7eb', 
                  borderRadius: '8px', 
                  resize: 'vertical',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  transition: 'all 0.2s',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '28px' }}>
              <button 
                className="btn btn-secondary" 
                onClick={handleCloseModal}
                disabled={submitting}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                  border: 'none',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                Cancelar
              </button>
              <button 
                className={modalType === 'approve' ? 'btn btn-success' : 'btn btn-danger'}
                onClick={handleSubmitModal}
                disabled={submitting}
                style={{
                  padding: '10px 24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  border: 'none',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                {submitting ? '⏳ Procesando...' : (modalType === 'approve' ? '✓ Aprobar' : '✕ Rechazar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRoutes;
